/**
 * W-8BEN Form Hook
 * Custom hook for managing W-8BEN form state using model fields as single source of truth
 */

import { useState, useEffect, useCallback } from "react";
import { W8BENFormModel } from "../Models/W8BENModels";
import { W8BENService } from "../services/W8BENService";

export interface UseW8BENFormOptions {
  initialData?: any;
}

export interface UseW8BENFormReturn {
  model: W8BENFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (field: string, value: string | boolean) => void;
  updateMultipleFields: (fields: Record<string, string | boolean>) => void;
  validate: () => boolean;
}

export const useW8BENForm = (options: UseW8BENFormOptions = {}): UseW8BENFormReturn => {
  const { initialData } = options;

  // State management using model as single source of truth
  const [model, setModel] = useState<W8BENFormModel>(() => {
    if (initialData) {
      return W8BENService.fromApiResponse(initialData);
    }
    return new W8BENFormModel();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Update validation state when model changes
  useEffect(() => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
  }, [model]);

  /**
   * Update a single field in the model
   */
  const updateField = useCallback((field: string, value: string | boolean) => {
    setModel((prevModel) => {
      const newModel = new W8BENFormModel(prevModel);
      (newModel as any)[field] = value;
      return newModel;
    });
  }, []);

  /**
   * Update multiple fields in the model
   */
  const updateMultipleFields = useCallback((fields: Record<string, string | boolean>) => {
    setModel((prevModel) => {
      const newModel = new W8BENFormModel(prevModel);
      Object.entries(fields).forEach(([field, value]) => {
        (newModel as any)[field] = value;
      });
      return newModel;
    });
  }, []);

  /**
   * Validate the current model
   */
  const validate = useCallback((): boolean => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
